import React from 'react';

import PropTypes from 'prop-types';

import { List } from 'reaxl';

import { hasContent } from '@/utilities/objectUtils';

import useVhrData from '@/hooks/useVhrData';

import VhrLink from '@/components/vehicledetails/VhrLink';

import Card from './Card';
import CardContentWithAnalytics from './CardContentWithAnalytics';
import { cardHeaders, cardImages } from './staticContent';

// The Data Prioritization following the list.
const vhrLabels = {
    SALVAGE_TITLE: 'Salvage Title',
    FRAME_DAMAGE: 'Frame Damage',
    FLOOD_WATER_DAMAGE: 'Flood Damage',
    ACCIDENTS_REPORTED: 'Accident or Damage Reported',
    NO_ACCIDENTS_REPORTED: 'No Accident or Damage Reported',
    NO_ODOMETER_CHECK: 'No Odometer Check',
    // VEHICLE_USE: 'Vehicle Use Type',
    ONE_OWNER: 'Single Owner',
    NO_ONE_OWNER: 'Multiple Owners',
    OPEN_RECALLS_REPORTED: 'At Least 1 Open Recall Reported',
    NO_OPEN_RECALLS_REPORTED: 'No Recalls Reported',
};

/**
 * Gets the appropriate card image based on tile type and theme
 *
 * @param {Object} params - Parameters for image selection
 * @param {boolean} params.isInverse - Whether using inverse theme
 * @param {boolean} params.shouldOverrideExperianTile - Whether to use KBB-branded Experian tile
 * @param {boolean} params.isExperianTile - Whether this is an Experian tile
 * @param {Object} params.vhrTile - VHR tile data containing image information
 * @returns {string} The image source URL
 */
const getCardImage = ({ isInverse, shouldOverrideExperianTile, isExperianTile, vhrTile }) => {
    if (shouldOverrideExperianTile) return isInverse ? cardImages.EXPERIAN_KBB_INVERSE : cardImages.EXPERIAN_KBB;
    if (isExperianTile) return isInverse ? cardImages.EXPERIAN_INVERSE : cardImages.EXPERIAN;
    return vhrTile?.image?.src;
};

/**
 * Gets the appropriate image dimensions based on tile override settings
 *
 * @param {Object} params - Parameters for image sizing
 * @param {boolean} params.shouldOverrideExperianTile - Whether using KBB-branded Experian tile
 * @returns {Object} Object containing height and width properties
 */
const getImageProps = ({ shouldOverrideExperianTile }) => {
    if (shouldOverrideExperianTile) return { height: 72, width: 90 };
    return { height: 38, width: 68 };
};

/**
 * VhrCard - Vehicle History Report card component for purchase confidence section
 *
 * This component renders a comprehensive Vehicle History Report card that displays VHR data
 * points in a list format along with a link to view the full report. It supports both
 * CARFAX and Experian/AutoCheck providers, handles email lead gate functionality, and
 * adapts its appearance based on various feature flags and tile configurations.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isPsx=false] - Whether this is a private seller (PSX) listing
 * @param {Object} [props.vhrTile={}] - VHR tile data containing tileType, link, image, etc.
 * @param {string[]} [props.vhrPreview] - Array of VHR data point keys to display
 * @param {Function} [props.onEmailCTAClick] - Callback for email CTA interactions in lead gate mode
 * @param {boolean} [props.overrideExperianTile] - Whether to use KBB-branded Experian tile
 * @param {boolean} [props.removeVhrCarfaxTile] - Whether to remove CARFAX tile props (experiment flag)
 * @param {string|number} props.listingId - The vehicle listing ID for analytics
 * @param {Function} [props.handleContentImpression] - Callback for content impression analytics
 * @param {boolean} [props.isInverse] - Whether to use inverse styling theme
 * @param {boolean} [props.shouldRedirectToExperianPage=false] - Whether to redirect to Experian page
 *
 * @returns {JSX.Element} The rendered VHR card component
 */
export default function VhrCard({
    isPsx = false,
    vhrTile = {},
    vhrPreview,
    onEmailCTAClick,
    overrideExperianTile,
    removeVhrCarfaxTile,
    listingId,
    handleContentImpression = () => { },
    isInverse,
    shouldRedirectToExperianPage = false,
    ...rest
}) {
    const {
        tileType,
        isCarfaxTile,
        labelKey,
        linkText,
        isLeadGateEnabled,
        isExperianTile,
        handleClick,
        showTile,
    } = useVhrData({
        vhrTile,
        listingId,
        isPsx,
        onEmailCtaClick: onEmailCTAClick,
        removeVhrCarfaxTile,
    });

    const handleContentImpressionWithTile = () => hasContent(vhrTile) && handleContentImpression(vhrTile);

    const vhrItems = Object.keys(vhrLabels).filter((item) => (vhrPreview).includes(item)).map((item) => vhrLabels[item]).slice(0, 3);
    const shouldOverrideExperianTile = overrideExperianTile && isExperianTile;
    const shouldRenderVhrLink = hasContent(vhrTile);

    const image = getCardImage({ isInverse, shouldOverrideExperianTile, isExperianTile, vhrTile });
    const imageProps = getImageProps({ shouldOverrideExperianTile });

    const handleImageTileClick = (event) => (isLeadGateEnabled ? onEmailCTAClick('vehicleHistoryReport') : handleClick(event));

    return (
        <Card
            id="vhr"
            heading={cardHeaders.VEHICLE_HISTORY_REPORT}
            image={image}
            imageProps={imageProps}
            isInverse={isInverse}
            handleClick={handleImageTileClick}
            linkLabel={linkText}
            showTile={showTile}
            {...(!isLeadGateEnabled && vhrTile?.link?.href && { link: vhrTile.link.href })}
            {...rest}
        >
            <CardContentWithAnalytics
                cardType={`vhr-${tileType?.toLowerCase()}`}
                handleContentImpression={handleContentImpressionWithTile}
                listingId={listingId}
            >
                {vhrItems.length > 0 && (
                    <List
                        className="margin-bottom-2"
                        items={vhrItems}
                    />
                )}
                {shouldRenderVhrLink && (
                    <VhrLink
                        labelKey={labelKey}
                        linkText={linkText}
                        isCarfaxTile={isCarfaxTile}
                        isInverse={isInverse}
                        isLeadGateEnabled={isLeadGateEnabled}
                        onClick={handleClick}
                        onEmailCtaClick={onEmailCTAClick}
                        removeVhrCarfaxTile={removeVhrCarfaxTile}
                        vhrTile={vhrTile}
                        {...(shouldRedirectToExperianPage && { listingId: String(listingId) })}
                    />
                )}
            </CardContentWithAnalytics>
        </Card>
    );
}

VhrCard.propTypes = {
    /** Whether to use inverse styling theme (light text on dark background) */
    isInverse: PropTypes.bool,

    /** Whether this is a private seller (PSX) listing for analytics tracking */
    isPsx: PropTypes.bool,

    /** VHR tile data containing tileType, epn, link, image, and other tile properties */
    vhrTile: PropTypes.object,

    /** Array of VHR data point keys to display (e.g., ['NO_ACCIDENTS_REPORTED', 'ONE_OWNER']) */
    vhrPreview: PropTypes.arrayOf(PropTypes.string),

    /** Callback function for email CTA interactions when lead gate is enabled */
    onEmailCTAClick: PropTypes.func,

    /** Whether to use KBB-branded Experian tile instead of standard Experian branding */
    overrideExperianTile: PropTypes.bool,

    /** Whether to remove CARFAX tile props (experiment flag for tile visibility control) */
    removeVhrCarfaxTile: PropTypes.bool,

    /** The vehicle listing ID used for analytics tracking */
    listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /** Callback function for content impression analytics when card becomes visible */
    handleContentImpression: PropTypes.func,

    /** Whether to redirect to the Experian page */
    shouldRedirectToExperianPage: PropTypes.bool,
};
